import React, { useEffect, useMemo, useRef, useState } from "react";
import "./RelatorioGoogleAds.css";

const API_GOOGLEADS = "/crm-wly/api/googleads";

async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
      "Content-Type": "application/json",
    },
  });

  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    const msg =
      (data && data.error) ||
      (data && data.message) ||
      (typeof data === "string" ? data : "Erro na requisição");
    throw new Error(msg);
  }
  return data;
}

function fmtBRDate(iso) {
  if (!iso) return "";
  // aceita "YYYY-MM-DD" ou "YYYY-MM-DD HH:mm:ss"
  const s = String(iso).trim().slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function kpiHelpText(key) {
  const map = {
    impressions: "Quantidade de vezes que seus anúncios apareceram",
    clicks: "Total de cliques nos seus anúncios",
    ctr: "Taxa de cliques (cliques ÷ impressões)",
    cost: "Investimento total em tráfego pago no período",
    average_cpc: "Custo médio por clique",
    conversions: "Total de conversões registradas",
    cost_per_conversion: "Custo médio por conversão",
    conversions_value: "Valor total atribuído às conversões",
  };
  return map[key] || "";
}

// isAdmin do seu app (localStorage)
function readIsAdminFromLocalStorage() {
  const raw = localStorage.getItem("isAdmin");
  if (raw !== null && raw !== undefined) {
    const v = String(raw).trim().toLowerCase();
    if (v === "true" || v === "1" || v === "yes") return true;
    if (v === "false" || v === "0" || v === "no") return false;
  }
  try {
    const userRaw = localStorage.getItem("usuario");
    if (userRaw) {
      const u = JSON.parse(userRaw);
      if (u?.isAdmin === true || u?.isAdmin === 1) return true;
      if (String(u?.tipo_usuario || "").toLowerCase() === "master") return true;
      if (String(u?.tipo || "").toLowerCase() === "master") return true;
    }
  } catch (_) {}
  return false;
}

// pega usuário logado (pra mostrar no filtro quando não é master)
async function fetchMeuUser() {
  // seu endpoint é /crm-wly/getUser.php (retorna o logado)
  const r = await fetchJSON("/crm-wly/getUser.php", { method: "GET" });
  // normalmente: { success:true, data:{...} }
  if (r?.data) return r.data;
  if (r?.success && r?.data) return r.data;
  return r;
}

// lista usuários (master)
async function fetchUsuariosListagem() {
  // você confirmou que existe esse endpoint
  const r = await fetchJSON("/crm-wly/listagem_usuarios.php", { method: "GET" });
  // retorna { tipo_usuario, usuarios:[...] }
  return Array.isArray(r?.usuarios) ? r.usuarios : [];
}

// helpers
function hojeISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function diasAtrasISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fmtBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtInt(n) {
  return Number(n || 0).toLocaleString("pt-BR");
}
function fmtPct(n) {
  const v = Number(n || 0) * 100;
  return `${v.toFixed(2).replace(".", ",")}%`;
}
function fmtNum(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}
function qs(params = {}) {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    u.set(k, String(v));
  });
  const s = u.toString();
  return s ? `?${s}` : "";
}

export default function RelatorioGoogleAds() {
  const isAdmin = useMemo(() => readIsAdminFromLocalStorage(), []);

  // filtros
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState(""); // string (select)
  const [usuarioLabel, setUsuarioLabel] = useState(""); // nome p/ topo

  const [dateStart, setDateStart] = useState(diasAtrasISO(30));
  const [dateEnd, setDateEnd] = useState(hojeISO());

  // token status
  const [tokenInfo, setTokenInfo] = useState({
    connected: false,
    access_token_valid: false,
    access_token_expires_at: null,
    loading: true,
    error: "",
  });

  // dados resumo
  const [resumo, setResumo] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [errorResumo, setErrorResumo] = useState("");

  const bootedRef = useRef(false);

  // 1) carregar usuários + setar default do filtro igual Meta
  useEffect(() => {
    (async () => {
      try {
        if (bootedRef.current) return;
        bootedRef.current = true;

        if (isAdmin) {
          const list = await fetchUsuariosListagem();
          setUsuarios(list);

          // default: vazio até o cara escolher (igual meta costuma fazer),
          // mas se quiser auto-selecionar o primeiro, descomenta abaixo:
          // if (list.length > 0) setUsuarioId(String(list[0].id));
        } else {
          const me = await fetchMeuUser();
          // força o filtro pro usuário logado
          setUsuarios([{ id: me?.id, nome: me?.nome, email: me?.email }]);
          setUsuarioId(String(me?.id || ""));
        }
      } catch (e) {
        // se der ruim aqui, ainda dá pra usar a página (mas sem select)
        console.error(e);
      }
    })();
  }, [isAdmin]);

  // label do usuário selecionado
  useEffect(() => {
    if (!usuarioId) {
      setUsuarioLabel("");
      return;
    }
    const u = usuarios.find((x) => String(x?.id) === String(usuarioId));
    setUsuarioLabel(u?.nome || u?.nome_empresa || u?.email || `Usuário ${usuarioId}`);
  }, [usuarios, usuarioId]);

  // 2) fluxo automático de token: status -> refresh se precisar
  async function ensureAccessToken(uId) {
    const uid = uId ? Number(uId) : null;

    // token_status GET
    const statusUrl = `${API_GOOGLEADS}/token_status.php${qs(
      isAdmin ? { usuario_id: uid } : {}
    )}`;

    const st = await fetchJSON(statusUrl, { method: "GET" });

    // esperado: { success:true, connected:true/false, access_token_valid, access_token_expires_at }
    const connected = !!st?.connected;
    const valid = !!st?.access_token_valid;

    if (!connected) {
      return {
        connected: false,
        access_token_valid: false,
        access_token_expires_at: st?.access_token_expires_at || null,
      };
    }

    if (valid) {
      return {
        connected: true,
        access_token_valid: true,
        access_token_expires_at: st?.access_token_expires_at || null,
      };
    }

    // refresh GET (você disse que mudou pra GET)
    const refreshUrl = `${API_GOOGLEADS}/token_refresh.php${qs(
      isAdmin ? { usuario_id: uid } : {}
    )}`;
    const rf = await fetchJSON(refreshUrl, { method: "GET" });

    return {
      connected: true,
      access_token_valid: true,
      access_token_expires_at: rf?.access_token_expires_at || null,
    };
  }

  async function carregarVisaoGeral() {
    try {
      setErrorResumo("");
      setLoadingResumo(true);
      setResumo(null);

      if (!usuarioId) {
        throw new Error("Selecione um usuário.");
      }

      // 2.1) token automático
      setTokenInfo((p) => ({ ...p, loading: true, error: "" }));
      try {
        const tk = await ensureAccessToken(usuarioId);
        setTokenInfo((p) => ({
          ...p,
          ...tk,
          loading: false,
          error: "",
        }));
        if (!tk.connected) {
          throw new Error(
            "Este usuário não tem integração ativa do Google Ads (vínculo não encontrado)."
          );
        }
      } catch (e) {
        setTokenInfo((p) => ({
          ...p,
          loading: false,
          error: e?.message || "Falha ao validar/renovar token",
        }));
        throw e;
      }

      // 2.2) chama relatorio_resumo (GET)
      const resumoUrl = `${API_GOOGLEADS}/relatorio_resumo.php${qs({
        // se seu relatorio_resumo já aceita usuario_id para master, ótimo.
        // se não aceitar, ele vai ignorar e usar a sessão.
        ...(isAdmin ? { usuario_id: Number(usuarioId) } : {}),
        date_start: dateStart,
        date_end: dateEnd,
      })}`;

      const r = await fetchJSON(resumoUrl, { method: "GET" });
      if (!r?.success) throw new Error(r?.error || "Falha ao carregar resumo");
      setResumo(r?.data || null);
    } catch (e) {
      setErrorResumo(e?.message || "Erro ao carregar visão geral");
    } finally {
      setLoadingResumo(false);
    }
  }

  // auto-load: quando tiver usuarioId (igual meta já carrega assim que escolhe)
  useEffect(() => {
    if (!usuarioId) return;
    // carrega sozinho ao trocar usuário (pra ficar “meta-like”)
    carregarVisaoGeral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId]);

  function aplicarPeriodo() {
    carregarVisaoGeral();
  }

  function resetPeriodo() {
    setDateStart(diasAtrasISO(30));
    setDateEnd(hojeISO());
    // e já recarrega
    setTimeout(() => carregarVisaoGeral(), 0);
  }

  const totais = resumo?.totais || null;

  return (
  <div className="gads-rel-page">
    {/* TÍTULO FORA DO CONTAINER (igual Meta) */}
    <h1 className="relatorio-meta-titulo">Relatório Google Ads</h1>


    <div className="gads-rel-card">
      {/* TOPO (agora sem repetir título grande dentro do card) */}
      <div className="gads-rel-top">
        <div className="gads-rel-titlewrap">
          <div className="gads-rel-sub">
            {usuarioLabel ? (
              <>
                Usuário: <b>{usuarioLabel}</b>
              </>
            ) : (
              "Selecione um usuário para ver o relatório."
            )}
          </div>
        </div>

        <div className="gads-rel-token">
          <div className="gads-rel-token-line">
            <span className="gads-rel-token-label">Token</span>
            {tokenInfo.loading ? (
              <span className="gads-rel-pill gads-rel-pill-warn">validando...</span>
            ) : tokenInfo.error ? (
              <span className="gads-rel-pill gads-rel-pill-err">erro</span>
            ) : tokenInfo.connected ? (
              tokenInfo.access_token_valid ? (
                <span className="gads-rel-pill gads-rel-pill-ok">ok</span>
              ) : (
                <span className="gads-rel-pill gads-rel-pill-warn">renovando...</span>
              )
            ) : (
              <span className="gads-rel-pill gads-rel-pill-warn">sem integração</span>
            )}
          </div>

          {tokenInfo.access_token_expires_at ? (
            <div className="gads-rel-token-exp">
              expira em: {String(tokenInfo.access_token_expires_at)}
            </div>
          ) : null}

          {tokenInfo.error ? (
            <div className="gads-rel-token-err">{tokenInfo.error}</div>
          ) : null}
        </div>
      </div>

            {/* FILTROS (igual Meta: usuário + período) */}
      <div className="gads-rel-filters">
        <div className="gads-rel-filter">
          <label>Selecione o Usuário:</label>
          <select
            value={usuarioId}
            onChange={(e) => setUsuarioId(e.target.value)}
            disabled={!usuarios.length}
          >
            {isAdmin ? (
              <>
                <option value="">-- selecione --</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome} {u.nome_empresa ? `(${u.nome_empresa})` : ""}
                  </option>
                ))}
              </>
            ) : (
              usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome || u.email || `Usuário ${u.id}`}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="gads-rel-filter gads-rel-filter-date">
          <label>Data Início:</label>
          <input
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
          />
        </div>

        <div className="gads-rel-filter gads-rel-filter-date">
          <label>Data Fim:</label>
          <input
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
          />
        </div>

        <div className="gads-rel-filter gads-rel-actions">
          <button
            type="button"
            className="gads-rel-btn gads-rel-btn-primary"
            onClick={aplicarPeriodo}
            disabled={loadingResumo}
          >
            Aplicar
          </button>

          <button
            type="button"
            className="gads-rel-btn gads-rel-btn-ghost"
            onClick={resetPeriodo}
          >
            Últimos 30 dias
          </button>
        </div>
      </div>

      {/* ALERTAS */}
      {errorResumo ? (
        <div className="gads-rel-alert gads-rel-alert-err">{errorResumo}</div>
      ) : null}


      {/* VISÃO GERAL (cards) */}
      <div className="gads-rel-section">
        <div className="gads-rel-section-title">Visão geral</div>

        {!totais && loadingResumo ? (
          <div className="gads-rel-loading">Carregando visão geral...</div>
        ) : null}

        {totais ? (
          <>
            <div className="gads-rel-kpis">
              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Impressões</div>
                <div className="gads-rel-kpi-value">{fmtInt(totais.impressions)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("impressions")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Cliques</div>
                <div className="gads-rel-kpi-value">{fmtInt(totais.clicks)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("clicks")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">CTR</div>
                <div className="gads-rel-kpi-value">{fmtPct(totais.ctr)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("ctr")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Custo</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.cost)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("cost")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">CPC médio</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.average_cpc)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("average_cpc")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Conversões</div>
                <div className="gads-rel-kpi-value">{fmtNum(totais.conversions)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("conversions")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Custo/Conversão</div>
                <div className="gads-rel-kpi-value">{fmtBRL(totais.cost_per_conversion)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("cost_per_conversion")}</div>
              </div>

              <div className="gads-rel-kpi">
                <div className="gads-rel-kpi-label">Valor conv.</div>
                <div className="gads-rel-kpi-value">{fmtNum(totais.conversions_value)}</div>
                <div className="gads-rel-kpi-desc">{kpiHelpText("conversions_value")}</div>
              </div>
            </div>

            <div className="gads-rel-footnote">
              Período: <b>{fmtBRDate(resumo?.periodo?.date_start)}</b> até{" "}
              <b>{fmtBRDate(resumo?.periodo?.date_end)}</b>
              {resumo?.customer?.customer_name ? (
                <>
                  {" "}
                  • Conta: <b>{resumo.customer.customer_name}</b>
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

        {/* Próximas seções virão depois (Campanhas, Grupos, Termos etc) */}
        <div className="gads-rel-section gads-rel-section-muted">
          <div className="gads-rel-section-title">Próximas seções</div>
          <div className="gads-rel-muted">
            Campanhas • Grupos • Anúncios • Termos de pesquisa • Localizações • etc.
          </div>
        </div>
      </div>
    </div>
  );
}
